import React from 'react';

import { useSelector } from 'react-redux';

import { toFabricUser } from 'src/components/conversation-provider/utils';
import { EditorSource } from 'src/components/editor/with-analytics';
import { Commit } from 'src/components/types';
import {
  ConversationEventHandlers,
  ConversationsContextProvider,
  buildConversationContext,
} from 'src/contexts/conversations-context';
import { useIntl } from 'src/hooks/intl';
import { getConversationsMentionsContext } from 'src/selectors/conversation-selectors';
import {
  getCurrentRepositoryOwnerName,
  getCurrentRepositorySlug,
  getCurrentRepositoryUuid,
} from 'src/selectors/repository-selectors';
import { getCurrentUser } from 'src/selectors/user-selectors';
import { getWorkspacePlan } from 'src/selectors/workspace-selectors';

import urls from '../urls';

export const ConversationContext = ({
  commit,
  children,
  eventHandlers,
  supportsCommentResolution,
}: {
  commit: Commit;
  children: React.ReactNode;
  eventHandlers: ConversationEventHandlers;
  supportsCommentResolution: boolean;
}): JSX.Element => {
  const intl = useIntl();
  const conversationsMentionContext = useSelector(
    getConversationsMentionsContext
  );

  const owner = useSelector(getCurrentRepositoryOwnerName) || '';
  const slug = useSelector(getCurrentRepositorySlug) || '';
  const repoId = useSelector(getCurrentRepositoryUuid) || undefined;
  const { isWorkspacePaid } = useSelector(getWorkspacePlan);
  const currentUser = useSelector(getCurrentUser);
  const { commentSave, commentUpdate, commentDelete } = urls.api.v20;

  const contextUrls = {
    commentSave: () => commentSave(owner, slug, commit.hash),
    commentUpdate: (commentId: number | string) =>
      commentUpdate(owner, slug, commit.hash, commentId),
    commentDelete: (commentId: number | string) =>
      commentDelete(owner, slug, commit.hash, commentId),
  };
  const contextValue = buildConversationContext({
    userAvatarUrl: currentUser?.links.avatar.href,
    userDisplayName: currentUser?.display_name,
    userProfileUrl: currentUser?.links.self.href,
    userUuid: currentUser?.uuid,
    fabricUser: toFabricUser(currentUser),
    urls: contextUrls,
    // TODO: Instrument these actions
    onAddComment: () => {},
    onDeleteComment: () => {},
    owner,
    slug,
    intl,
    conversationsMentionContext,
    renderAdditionalActions: false,
    eventHandlers,
    supportsCommentResolution,
    repoId,
    workspacePaid: isWorkspacePaid,
    editorSource: EditorSource.COMMIT_CONVERSATION,
  });

  return (
    <ConversationsContextProvider value={contextValue}>
      {children}
    </ConversationsContextProvider>
  );
};
